import { createRouter, createWebHistory } from 'vue-router'
import LoginView from '../views/LoginView.vue'
import RegisterView from '../views/RegisterView.vue'
import ForgotPasswordView from '../views/ForgotPasswordView.vue'
import ResetPasswordView from '../views/ResetPasswordView.vue'
import HomeView from '../views/HomeView.vue'
import MyPayoutsView from '../views/MyPayoutsView.vue'
import AccountView from '../views/AccountView.vue'
import HelpCenter from '../views/HelpCenter.vue'
import ExpertsView from '../views/ExpertsView.vue'
import ExpertProfileView from '../views/ExpertProfileView.vue'
import VisualIdentitySystemsView from '../views/VisualIdentitySystemsView.vue'
import AssessmentsView from '../views/AssessmentsView.vue'
import ResponsesView from '../views/ResponsesView.vue'
import AdminPaymentsView from '../views/AdminPaymentsView.vue'
import store from '../store'
import { getAuthToken, getRefreshToken, isTokenExpired } from '@/utils/auth'

const routes = [
  {
    path: '/',
    name: 'root',
    redirect: (to) => {
      // Check if user is admin and redirect accordingly
      const user = store.state.user;
      if (user && (user.is_staff || user.is_superuser)) {
        return { name: 'assessments' };
      }
      return { name: 'home' };
    }
  },
  {
    path: '/login',
    name: 'login',
    component: LoginView,
    meta: { 
      requiresAuth: false,
      layout: 'auth'
    }
  },
  {
    path: '/register',
    name: 'register',
    component: RegisterView,
    meta: { 
      requiresAuth: false,
      layout: 'auth'
    }
  },
  {
    path: '/forgot-password',
    name: 'forgotPassword',
    component: ForgotPasswordView,
    meta: { 
      requiresAuth: false,
      layout: 'auth'
    }
  },
  {
    path: '/reset-password',
    name: 'resetPassword',
    component: ResetPasswordView,
    meta: { 
      requiresAuth: false,
      layout: 'auth'
    }
  },
  {
    path: '/home',
    name: 'home',
    component: HomeView,
    meta: { requiresAuth: true }
  },
  {
    path: '/my-payouts',
    name: 'myPayouts',
    component: MyPayoutsView,
    meta: { requiresAuth: true }
  },
  {
    path: '/account',
    name: 'account',
    component: AccountView,
    meta: { requiresAuth: true }
  },
  {
    path: '/help',
    name: 'HelpCenter',
    component: HelpCenter,
    meta: { requiresAuth: true }
  },
  {
    path: '/experts',
    name: 'experts',
    component: ExpertsView,
    meta: { requiresAuth: true }
  },
  {
    path: '/experts/:id',
    name: 'expertProfile',
    component: ExpertProfileView,
    meta: { requiresAuth: true }
  },
  {
    path: '/vis',
    name: 'visualIdentitySystems',
    component: VisualIdentitySystemsView,
    meta: { requiresAuth: true }
  },
  {
    path: '/vis/:id',
    name: 'visDetail',
    component: () => import('../views/VISDetailView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/assessments',
    name: 'assessments',
    component: AssessmentsView,
    meta: { requiresAuth: true }
  },
  {
    path: '/assessments/create',
    name: 'createAssessment',
    component: () => import('../views/CreateAssessmentView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/assessments/:id',
    name: 'assessmentDetail',
    component: () => import('../views/AssessmentDetailView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/assessments/:id/edit',
    name: 'editAssessment',
    component: () => import('../views/EditAssessmentView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/assessments/:id/complete',
    name: 'completeAssessment',
    component: () => import('../views/CompleteAssessmentView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/assessments/:id/preview',
    name: 'previewAssessment',
    component: () => import('../views/CompleteAssessmentView.vue'),
    meta: { requiresAuth: true, preview: true }
  },
  {
    path: '/responses',
    name: 'responses',
    component: ResponsesView,
    meta: { requiresAuth: true }
  },
  {
    path: '/responses/:id',
    name: 'responseDetail',
    component: () => import('../views/ResponseDetailView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/responses/:id/print',
    name: 'printReport',
    component: () => import('../views/PrintReportView.vue'),
    meta: { 
      requiresAuth: true,
      layout: 'print' 
    }
  },
  {
    path: '/admin/payments',
    name: 'adminPayments',
    component: AdminPaymentsView,
    meta: { requiresAuth: true }
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

// Navigation guard
router.beforeEach(async (to, from, next) => {
  // Check for auth tokens directly rather than relying on store state
  // which might not be initialized yet during page refresh
  const authToken = getAuthToken();
  const refreshToken = getRefreshToken();
  
  // Debug router navigation
  console.log('Navigation guard - Auth token exists:', !!authToken);
  console.log('Navigation guard - Target route:', to.path);
  
  // Check if user has tokens but store state is not yet loaded
  if (authToken && refreshToken && !store.state.user) {
    try {
      // If token is expired, try to refresh it first
      if (isTokenExpired()) {
        console.log('Token is expired, attempting to refresh');
        await store.dispatch('refreshToken');
      }
      
      // Try to load user profile if not already loaded
      if (!store.state.user) {
        console.log('Loading user profile');
        await store.dispatch('fetchUserProfile');
      }
    } catch (error) {
      console.error('Authentication error in router guard:', error);
      // Only clear auth on 401 errors
      if (error.response && error.response.status === 401) {
        store.commit('CLEAR_AUTH');
      }
    }
  }
  
  // Now check if user is authenticated based on store state
  const isAuthenticated = store.getters.isAuthenticated;
  const user = store.state.user;
  const isAdmin = user && (user.is_staff || user.is_superuser);
  
  if (to.meta.requiresAuth && !isAuthenticated) {
    // If route requires auth and user is not authenticated
    next({ name: 'login' });
  } else if ((to.name === 'login' || to.name === 'register' || to.name === 'forgotPassword' || to.name === 'resetPassword') && isAuthenticated) {
    // If user is already authenticated and tries to access login/register/password recovery
    // Redirect based on user role
    if (isAdmin) {
      next({ name: 'assessments' });
    } else {
      next({ name: 'home' });
    }
  } else if (to.name === 'home' && isAdmin) {
    // Redirect admins from home to assessments
    next({ name: 'assessments' });
  } else {
    // In all other cases, allow navigation
    next();
  }
})

export default router 